import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { getCurrencySymbol } from '../../utils/currency';
import {
  Check, X, Tag, Plus, CaretDown, CaretRight, Image,
  ListBullets, ToggleRight, ToggleLeft, Trash, CaretUp
} from '@phosphor-icons/react';

const CATEGORY_NAMES = {
  parking: 'Parking', hotels: 'Hotels / Stay', gyms: 'Gyms / Fitness',
  yoga_wellness: 'Yoga / Wellness',
  hospital: 'Hospital / Clinic', home: 'Home / Private', coworking: 'Coworking / Office',
  meeting_rooms: 'Meeting Rooms', event_spaces: 'Event Spaces', studios: 'Studios',
  storage: 'Storage', shops: 'Shops / Retail', malls: 'Malls / Kiosks',
  halls: 'Halls / Banquet', housing_societies: 'Housing Societies',
  religious_places: 'Religious Places', salons: 'Salons / Parlors', clubs: 'Clubs',
  restaurants: 'Restaurants', sports: 'Sports / Turf', ev_charging: 'EV Charging', education: 'Education'
};

const CategoriesView = ({
  categories, expandedCat, catServices, loadingSvcs, editingIcon, iconUrl,
  showAddCategory, newCatName, newCatIcon, showAddService, newSvcName, newSvcPrice, newSvcDuration,
  toggleExpand, handleToggleCategory, handleSaveIcon, handleAddCategory, handleDeleteCategory,
  handleAddService, handleToggleService, handleDeleteService, handleMoveCategory, handleReorderCategories,
  setEditingIcon, setIconUrl, setShowAddCategory, setNewCatName, setNewCatIcon,
  setShowAddService, setNewSvcName, setNewSvcPrice, setNewSvcDuration
}) => {
  const [dragId, setDragId] = React.useState(null);
  const [overId, setOverId] = React.useState(null);

  const handleDrop = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    const ids = categories.map(c => c.category_id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    if (typeof handleReorderCategories === 'function') handleReorderCategories(ids);
    setDragId(null); setOverId(null);
  };

  return (
  <div>
    <div className="flex items-center justify-between mb-6">
      <p className="text-[#6b7a99] text-sm">
        {categories.filter(c => c.active !== false).length} active, {categories.filter(c => c.active === false).length} inactive
      </p>
      <Button onClick={() => setShowAddCategory(true)} className="bg-indigo-600 hover:bg-indigo-700 rounded-xl text-sm gap-1.5" data-testid="add-category-btn">
        <Plus size={16} /> Add Category
      </Button>
    </div>

    {/* Add Category Form */}
    {showAddCategory && (
      <div className="bg-[#1a2033] border border-[#252d3d] rounded-2xl p-4 mb-4" data-testid="add-category-form">
        <h3 className="text-white font-semibold text-sm mb-3">New Category</h3>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <label className="text-[#6b7a99] text-xs mb-1 block">Name</label>
            <Input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="Category name" className="bg-[#0f1219] border-[#252d3d] text-white h-9 rounded-lg text-sm" data-testid="new-cat-name" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-[#6b7a99] text-xs mb-1 block">Icon URL (optional)</label>
            <Input value={newCatIcon} onChange={e => setNewCatIcon(e.target.value)} placeholder="https://..." className="bg-[#0f1219] border-[#252d3d] text-white h-9 rounded-lg text-sm" data-testid="new-cat-icon" />
          </div>
          <Button onClick={handleAddCategory} className="bg-emerald-600 hover:bg-emerald-700 h-9 rounded-lg text-sm" data-testid="save-new-cat">Create</Button>
          <Button variant="ghost" onClick={() => setShowAddCategory(false)} className="text-[#6b7a99] h-9 rounded-lg text-sm">Cancel</Button>
        </div>
      </div>
    )}

    {/* Category List */}
    <div className="space-y-1.5" data-testid="categories-list">
      {categories.map(cat => (
        <div key={cat.category_id} className="group" data-testid={`cat-${cat.category_id}`}
          draggable
          onDragStart={(e) => { setDragId(cat.category_id); e.dataTransfer.effectAllowed = 'move'; }}
          onDragOver={(e) => { e.preventDefault(); if (overId !== cat.category_id) setOverId(cat.category_id); }}
          onDragLeave={() => { if (overId === cat.category_id) setOverId(null); }}
          onDrop={(e) => { e.preventDefault(); handleDrop(cat.category_id); }}
          onDragEnd={() => { setDragId(null); setOverId(null); }}
          style={{ cursor: 'grab', opacity: dragId === cat.category_id ? 0.4 : 1 }}>
          {/* Drop indicator */}
          {overId === cat.category_id && dragId && dragId !== cat.category_id && (
            <div className="h-0.5 bg-indigo-500 rounded-full mb-1" data-testid={`drop-indicator-${cat.category_id}`} />
          )}
          {/* Category Row */}
          <div className={`bg-[#161b26] border rounded-xl transition-all ${expandedCat === cat.category_id ? 'border-indigo-500/40 rounded-b-none' : 'border-[#252d3d]'} ${cat.active === false ? 'opacity-50' : ''}`}>
            <div className="flex items-center gap-3 px-4 py-3">
              {/* Reorder controls */}
              <div className="flex flex-col -my-1">
                <button onClick={() => handleMoveCategory(cat.category_id, 'up')}
                  className="text-[#6b7a99] hover:text-white transition-colors disabled:opacity-20"
                  disabled={categories[0]?.category_id === cat.category_id}
                  title="Move up" data-testid={`move-up-${cat.category_id}`}>
                  <CaretUp size={13} weight="bold" />
                </button>
                <button onClick={() => handleMoveCategory(cat.category_id, 'down')}
                  className="text-[#6b7a99] hover:text-white transition-colors disabled:opacity-20"
                  disabled={categories[categories.length - 1]?.category_id === cat.category_id}
                  title="Move down" data-testid={`move-down-${cat.category_id}`}>
                  <CaretDown size={13} weight="bold" />
                </button>
              </div>
              {/* Expand Arrow */}
              <button onClick={() => toggleExpand(cat.category_id)} className="text-[#6b7a99] hover:text-white transition-colors" data-testid={`expand-${cat.category_id}`}>
                {expandedCat === cat.category_id ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
              </button>

              {/* Icon */}
              <div className="w-9 h-9 bg-[#1e2536] rounded-lg flex items-center justify-center shrink-0 overflow-hidden">
                {cat.icon_url ? <img src={cat.icon_url} alt="" className="w-full h-full object-cover rounded-lg" /> : <Tag size={18} className="text-[#6b7a99]" />}
              </div>

              {/* Name */}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm">{CATEGORY_NAMES[cat.category_id] || cat.name || cat.category_id}</p>
                <div className="flex items-center gap-3 text-[10px] text-[#6b7a99]">
                  <span>{cat.category_id}</span>
                  <span>{cat.services_count || 0} services</span>
                  <span>{cat.listings_count || 0} listings</span>
                  {cat.custom && <span className="text-amber-400">custom</span>}
                </div>
              </div>

              {/* Active Badge */}
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${cat.active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                {cat.active !== false ? 'Active' : 'Inactive'}
              </span>

              {/* Icon Edit */}
              {editingIcon === cat.category_id ? (
                <div className="flex items-center gap-1">
                  <Input value={iconUrl} onChange={e => setIconUrl(e.target.value)} placeholder="Image URL" className="h-7 w-40 text-xs bg-[#0f1219] border-[#252d3d] text-white rounded-lg" data-testid={`icon-input-${cat.category_id}`} />
                  <button onClick={() => handleSaveIcon(cat.category_id)} className="p-1 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-md" data-testid={`save-icon-${cat.category_id}`}><Check size={12} className="text-emerald-400" weight="bold" /></button>
                  <button onClick={() => { setEditingIcon(null); setIconUrl(''); }} className="p-1 bg-[#252d3d] hover:bg-[#303a4f] rounded-md"><X size={12} className="text-[#6b7a99]" /></button>
                </div>
              ) : (
                <button onClick={() => { setEditingIcon(cat.category_id); setIconUrl(cat.icon_url || ''); }} className="p-1.5 text-[#6b7a99] hover:text-blue-400 hover:bg-blue-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" title="Change Icon" data-testid={`icon-btn-${cat.category_id}`}>
                  <Image size={16} />
                </button>
              )}

              {/* Toggle */}
              <button onClick={() => handleToggleCategory(cat.category_id)} className="p-1" title="Toggle Active" data-testid={`toggle-cat-${cat.category_id}`}>
                {cat.active !== false ? <ToggleRight size={24} className="text-emerald-400" weight="fill" /> : <ToggleLeft size={24} className="text-[#6b7a99]" weight="fill" />}
              </button>

              {/* Delete (custom only) */}
              {cat.custom && (
                <button onClick={() => handleDeleteCategory(cat.category_id)} className="p-1.5 text-[#6b7a99] hover:text-red-400 hover:bg-red-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-cat-${cat.category_id}`}>
                  <Trash size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Expanded Services */}
          {expandedCat === cat.category_id && (
            <div className="bg-[#0f1219] border border-t-0 border-indigo-500/40 rounded-b-xl px-4 py-3" data-testid={`services-${cat.category_id}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-[#a5b4d0] text-xs font-semibold uppercase tracking-wider">Default Services</h4>
                <button onClick={() => setShowAddService(cat.category_id)} className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 text-xs font-medium" data-testid={`add-svc-${cat.category_id}`}>
                  <Plus size={14} /> Add Service
                </button>
              </div>

              {/* Add Service Form */}
              {showAddService === cat.category_id && (
                <div className="bg-[#161b26] rounded-lg p-3 mb-3 flex gap-2 items-end flex-wrap" data-testid={`add-svc-form-${cat.category_id}`}>
                  <div className="flex-1 min-w-[140px]">
                    <label className="text-[#6b7a99] text-[10px] mb-1 block">Service Name</label>
                    <Input value={newSvcName} onChange={e => setNewSvcName(e.target.value)} placeholder="e.g. Premium Wash" className="bg-[#0f1219] border-[#252d3d] text-white h-8 text-xs rounded-lg" data-testid="new-svc-name" />
                  </div>
                  <div className="w-24">
                    <label className="text-[#6b7a99] text-[10px] mb-1 block">Price</label>
                    <Input value={newSvcPrice} onChange={e => setNewSvcPrice(e.target.value)} placeholder="0" type="number" className="bg-[#0f1219] border-[#252d3d] text-white h-8 text-xs rounded-lg" data-testid="new-svc-price" />
                  </div>
                  <div className="w-24">
                    <label className="text-[#6b7a99] text-[10px] mb-1 block">Duration (min)</label>
                    <Input value={newSvcDuration} onChange={e => setNewSvcDuration(e.target.value)} placeholder="60" type="number" className="bg-[#0f1219] border-[#252d3d] text-white h-8 text-xs rounded-lg" data-testid="new-svc-duration" />
                  </div>
                  <Button onClick={() => handleAddService(cat.category_id)} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs rounded-lg" data-testid="save-new-svc">Add</Button>
                  <Button variant="ghost" onClick={() => setShowAddService(null)} className="text-[#6b7a99] h-8 text-xs">Cancel</Button>
                </div>
              )}

              {loadingSvcs === cat.category_id ? (
                <div className="py-4 text-center"><div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin mx-auto" /></div>
              ) : (catServices[cat.category_id] || []).length > 0 ? (
                <div className="space-y-1">
                  {(catServices[cat.category_id] || []).map(svc => (
                    <div key={svc.service_id} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg ${svc.active !== false ? 'bg-[#161b26]' : 'bg-[#161b26] opacity-50'}`} data-testid={`svc-${svc.service_id}`}>
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center ${svc.active !== false ? 'bg-emerald-500/15' : 'bg-red-500/15'}`}>
                        <ListBullets size={12} className={svc.active !== false ? 'text-emerald-400' : 'text-red-400'} />
                      </div>
                      <span className="text-white text-xs font-medium flex-1">{svc.name}</span>
                      {svc.price > 0 && <span className="text-[#a5b4d0] text-[10px]">{getCurrencySymbol()}{svc.price}</span>}
                      {svc.duration_minutes && <span className="text-[#6b7a99] text-[10px]">{svc.duration_minutes}m</span>}
                      <button onClick={() => handleToggleService(cat.category_id, svc.service_id)} className="p-0.5" data-testid={`toggle-svc-${svc.service_id}`}>
                        {svc.active !== false ? <ToggleRight size={18} className="text-emerald-400" weight="fill" /> : <ToggleLeft size={18} className="text-[#6b7a99]" weight="fill" />}
                      </button>
                      <button onClick={() => handleDeleteService(cat.category_id, svc.service_id)} className="p-0.5 text-[#6b7a99] hover:text-red-400" data-testid={`del-svc-${svc.service_id}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#6b7a99] text-xs text-center py-3">No default services. Click "Add Service" to create one.</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
  );
};

export default CategoriesView;
